import { useState } from "react";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import { ProductMixMonth } from "src/store/api/generatedApi";
import { formatMixPercentage } from "./useTenantProductMix";

export const TenantProductMixDisplay = ({
  mixGroupNames,
  months,
}: {
  mixGroupNames: string[];
  months: ProductMixMonth[];
}) => {
  const theme = useTheme();
  const [monthIndex, setMonthIndex] = useState(0);

  const dataColors = [
    theme.palette.data.blue,
    theme.palette.data.orange,
    theme.palette.data.green,
  ];

  const clampedIndex = Math.min(monthIndex, Math.max(months.length - 1, 0));
  const current = months[clampedIndex];
  if (!current) return null;

  const groupsByName = Object.fromEntries(
    current.groups.map((g) => [g.mix_group_name, g])
  );
  const allGroups = mixGroupNames.map((name) => ({
    mix_group_name: name,
    mix_percentage: groupsByName[name]?.mix_percentage ?? 0,
    produced_mass_tonnes: groupsByName[name]?.produced_mass_tonnes ?? 0,
  }));

  return (
    <Stack gap={0.25}>
      <Stack direction="row" gap={1.5} justifyContent="space-between">
        {allGroups.map((g, i) => (
          <Stack
            key={g.mix_group_name}
            alignItems="center"
            gap={0}
            sx={{ flex: 1 }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: dataColors[i % dataColors.length] }}
            >
              {g.mix_group_name}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formatMixPercentage(g.mix_percentage)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="center">
        <IconButton
          size="small"
          disabled={clampedIndex >= months.length - 1}
          onClick={(e) => {
            e.stopPropagation();
            setMonthIndex((i) => Math.min(i + 1, months.length - 1));
          }}
          sx={{ p: 0.25 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {dayjs(current.month_start).format("MMM YYYY")}
        </Typography>
        <IconButton
          size="small"
          disabled={clampedIndex <= 0}
          onClick={(e) => {
            e.stopPropagation();
            setMonthIndex((i) => Math.max(i - 1, 0));
          }}
          sx={{ p: 0.25 }}
        >
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
